/**
 * 字典数据池：按 type 拉取树形数据 + 扁平映射与父级链，带缓存
 * 用于 DictionaryDisplay、DictionarySelect 等组件的统一数据源
 */
import { getDisplayTextByLocale } from '@/utils/locale';
import { listDictionariesTree } from './dictionary';

/** 字典节点 + 从根到当前节点的父级链（不含自身） */
export interface DictionaryNodeWithParents {
  node: API.DictionaryTreeResponse;
  parentChain: API.DictionaryTreeResponse[];
}

/** 数据池返回：树 + id -> 节点与父级链 */
export interface DictionaryTypeData {
  type: string;
  tree: API.DictionaryTreeResponse[];
  /** id -> 节点与从根到其父级的链 */
  mapById: Map<string, DictionaryNodeWithParents>;
}

/** 展示名称：按当前语言优先 display_text，否则 value/code */
export function getDictionaryDisplayName(node: API.DictionaryTreeResponse): string {
  const text = getDisplayTextByLocale(
    node?.display_text as Record<string, string> | undefined,
    '-',
  );
  if (text !== '-') return text;
  return node?.value ?? node?.code ?? '-';
}

const cache = new Map<string, DictionaryTypeData>();
let loadPromises = new Map<string, Promise<DictionaryTypeData>>();

function buildFlatMap(
  tree: API.DictionaryTreeResponse[],
  parentChain: API.DictionaryTreeResponse[] = [],
): Map<string, DictionaryNodeWithParents> {
  const map = new Map<string, DictionaryNodeWithParents>();
  const walk = (
    nodes: API.DictionaryTreeResponse[],
    parents: API.DictionaryTreeResponse[],
  ) => {
    for (const node of nodes) {
      map.set(node.id, { node, parentChain: [...parents] });
      if (node.children?.length) {
        walk(node.children, [...parents, node]);
      }
    }
  };
  walk(tree, []);
  return map;
}

/**
 * 按类型获取字典树数据（树 + mapById），带内存缓存
 */
export async function getDictionaryDataByType(
  type: string,
  isActive: boolean = true,
): Promise<DictionaryTypeData> {
  const cacheKey = `${type}_${isActive}`;
  const cached = cache.get(cacheKey);
  if (cached) return cached;

  const existing = loadPromises.get(cacheKey);
  if (existing) return existing;

  const promise = (async (): Promise<DictionaryTypeData> => {
    const res = await listDictionariesTree({
      type,
      is_active: isActive ? true : undefined,
    });
    const tree: API.DictionaryTreeResponse[] =
      res.code === 0 && res.data?.list?.length ? res.data.list : [];
    const mapById = buildFlatMap(tree);
    const data: DictionaryTypeData = { type, tree, mapById };
    cache.set(cacheKey, data);
    return data;
  })();

  loadPromises.set(cacheKey, promise);
  try {
    return await promise;
  } finally {
    loadPromises.delete(cacheKey);
  }
}

/** 清除字典数据缓存（可选，如配置页增删改后希望刷新） */
export function clearDictionaryDataCache(type?: string): void {
  if (type) {
    for (const key of cache.keys()) {
      if (key.startsWith(`${type}_`)) cache.delete(key);
    }
  } else {
    cache.clear();
  }
  loadPromises = new Map();
}
